import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import useTranslation from "@babackapp/translations/use-translation";
import useAddShopMembers from "@babackapp/private-api-client/client/use-add-shop-members";
import {
  AtSymbolIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useState } from "react";
import PasswordStrengthBar from "react-password-strength-bar";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@babackapp/auth-client/hooks";
import { Input } from "@babackapp/ui/components/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { Button } from "@babackapp/ui/components/button";
import { jwtDecode } from "jwt-decode";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import logo from "@/assets/logo.png";

const passwordSchema = z
  .string()
  .min(8, { message: "Le mot de passe doit contenir au moins 8 caractères." })
  .regex(/[A-Z]/, {
    message: "Le mot de passe doit contenir au moins une lettre majuscule.",
  })
  .regex(/[a-z]/, {
    message: "Le mot de passe doit contenir au moins une lettre minuscule.",
  })
  .regex(/[0-9]/, {
    message: "Le mot de passe doit contenir au moins un chiffre.",
  })
  .regex(/[^A-Za-z0-9]/, {
    message: "Le mot de passe doit contenir au moins un caractère spécial.",
  });

const signUpSchema = z.object({
  firstName: z.string().nonempty({ message: "Le prénom est requis." }),
  lastName: z.string().nonempty({ message: "Le nom est requis." }),
  email: z.string().email({ message: "Adresse e-mail invalide." }),
  password: passwordSchema,
});

type SignUpFormValues = z.infer<typeof signUpSchema>;

export default function SignUpPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const onSuccess = useCallback(() => {
    navigate(`/`, { replace: true });
  }, []);
  const onError = useCallback(() => {
    navigate("/auth/sign-in");
  }, []);
  const { mutate: addShopMember } = useAddShopMembers({
    onSuccess,
    onError,
  });

  const token = new URLSearchParams(window.location.search).get("token");
  const emailFromUrl = new URLSearchParams(window.location.search).get("email");
  const [inputPasswordType, setInputPasswordType] = useState<
    "password" | "text"
  >("password");
  const [passwordScore, setPasswordScore] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: emailFromUrl || "",
      firstName: "",
      lastName: "",
      password: "",
    },
  });

  const password = watch("password", "");

  const createAccount = async (data: SignUpFormValues) => {
    try {
      // Register user with new auth system (plain text password)
      const createdUser = await auth.register({
        email: data.email.toLowerCase(),
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      // Handle shop member invitation if token exists
      if (token) {
        const { shopId } = jwtDecode(token as string) as {
          shopId: string;
        };
        addShopMember({
          shopId,
          requestBody: {
            token,
            userId: createdUser.userId,
          },
        });
      }

      // Registration successful, navigate to sign-up success page
      navigate("/auth/sign-up-success", {
        state: { email: data.email.toLowerCase() },
      });
    } catch (err) {
      console.error(err);
      // Error handling is managed by auth context
    }
  };

  const passwordRequirements = [
    t("signup.passwordRequirementsList.title"),
    t("signup.passwordRequirementsList.length"),
    t("signup.passwordRequirementsList.uppercase"),
    t("signup.passwordRequirementsList.lowercase"),
    t("signup.passwordRequirementsList.number"),
    t("signup.passwordRequirementsList.special"),
    t("signup.passwordRequirementsList.strength"),
  ];

  return (
    <div className="h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <img className="h-12 w-auto" src={logo} alt="Baback" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              {t("signup.title")}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t("misc.or")}{" "}
              <Link
                to={`/auth/sign-in${token || emailFromUrl ? "?" : ""}${
                  token ? "token=" + token : ""
                }${emailFromUrl ? "&email=" + emailFromUrl : ""}`}
                className="font-medium text-purple-600 hover:text-purple-500"
              >
                {t("login.login")}
              </Link>
            </p>
          </div>

          <div className="mt-8">
            {auth.error && (
              <FormGlobalError
                error={auth.error}
                email={watch("email")}
                onResendVerification={auth.resendVerification}
              />
            )}
          </div>

          <div className="mt-8 space-y-6">
            <form onSubmit={handleSubmit(createAccount)}>
              <div className="flex space-x-5">
                <div className="flex-1">
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("accountSettings.firstName")}
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    {...register("firstName")}
                    className="mt-1 block w-full"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="flex-1">
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("accountSettings.name")}
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    {...register("lastName")}
                    className="mt-1 block w-full"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("login.email")}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <AtSymbolIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    {...register("email")}
                    className="pl-10"
                    disabled={!!emailFromUrl && !token}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div className="mt-4">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("accountSettings.password")}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <Input
                    id="password"
                    type={inputPasswordType}
                    {...register("password")}
                    className="pl-10 pr-20"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() =>
                        setInputPasswordType(
                          inputPasswordType === "password"
                            ? "text"
                            : "password",
                        )
                      }
                      className="text-sm text-gray-600 hover:text-gray-800 focus:outline-none"
                    >
                      {inputPasswordType === "password"
                        ? t("accountSettings.show")
                        : t("returnDetail.hide")}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>
              {password.length > 0 && (
                <div className="mt-2">
                  <PasswordStrengthBar
                    password={password}
                    onChangeScore={(score) => setPasswordScore(score)}
                  />
                  {passwordScore < 3 && (
                    <p className="mt-1 text-sm text-orange-500">
                      {t("signup.passwordTooWeak")}
                    </p>
                  )}
                </div>
              )}
              <div className="mt-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-1 cursor-pointer">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400" />
                      <p className="text-sm text-gray-400">
                        {t("signup.passwordRequirements")}
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="p-2">
                      {passwordRequirements.map((requirement, index) => (
                        <div key={index} className="mb-1">
                          {requirement}
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm">
                  <a
                    href="/app/login/forgotpassword"
                    className="font-medium text-purple-600 hover:text-purple-500"
                  >
                    {t("login.forgotPwd")}
                  </a>
                </div>
              </div>
              <div className="mt-6">
                <Button
                  type="submit"
                  disabled={
                    auth.isLoading || (password.length > 0 && passwordScore < 3)
                  }
                  className="w-full"
                >
                  {t("signup.title")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative w-0 flex-1">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1505904267569-f02eaeb45a4c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1908&q=80"
          alt=""
        />
      </div>
    </div>
  );
}
